/* eslint-disable react/no-unescaped-entities */
/**
 * /verifier-un-contrat, /verifier-un-bail, /verifier-une-facture,
 * /verifier-un-devis, /verifier-documents-entreprise
 *
 * Pages SEO ciblées de l'Univers Vérification, qui redirigent visuellement
 * vers le Vérificateur tout en exposant le contenu spécifique pour Google.
 */
import { Link } from "react-router-dom";
import {
  ScanLine, ShieldCheck, GitMerge, Compass, ArrowRight, FileText,
  Home, Receipt, FileSpreadsheet, Landmark, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb, ldFAQ } from "@/components/Seo";


export const VERIFIER_PAGES = {
  "verifier-un-contrat": {
    h1: "Faire vérifier un contrat avant signature ou litige",
    seo_title: "Vérifier un contrat avec le Vérificateur CONFILEX",
    seo_description: "Audit documentaire intelligent d'un contrat : lecture factuelle, détection de divergences, points à clarifier, questions à préparer pour un professionnel. Sans avis juridique.",
    icon: FileText,
    intent_keywords: ["vérifier un contrat", "audit de contrat", "analyse de contrat", "contrat à signer"],
    description: "Le Vérificateur CONFILEX produit une lecture factuelle de votre contrat : parties, montants, dates clés, clauses sensibles, points à clarifier. Aucune qualification juridique — uniquement de l'organisationnel.",
    bullets: [
      "Identification des parties et de leur rôle déclaré",
      "Surlignage des clauses sensibles (durée, exclusion, pénalité, reconduction)",
      "Chronologie des dates clés (signature, effet, résiliation, échéance)",
      "Points à clarifier avant signature",
      "Questions à préparer pour votre conseil professionnel",
    ],
    vertical_id: "precontentieux",
    faq: [
      { q: "Le Vérificateur donne-t-il un avis juridique sur le contrat ?", a: "Non. CONFILEX produit une lecture documentaire factuelle. Aucune qualification de validité, de nullité ou d'opposabilité n'est délivrée. Une consultation juridique reste du ressort exclusif d'un avocat." },
      { q: "Combien de temps prend l'analyse d'un contrat ?", a: "Environ 15 secondes pour un PDF natif avec couche texte. Le rapport intègre un résumé, des passages surlignés, une chronologie, les acteurs identifiés et les points à clarifier." },
    ],
  },
  "verifier-un-bail": {
    h1: "Vérifier un bail avant signature",
    seo_title: "Vérifier un bail (habitation, commercial) — CONFILEX",
    seo_description: "Audit documentaire d'un bail d'habitation ou commercial : loyer, charges, dépôt de garantie, surface, diagnostics, conditions particulières. Analyse strictement factuelle.",
    icon: Home,
    intent_keywords: ["vérifier un bail", "audit de bail", "bail commercial", "bail d'habitation"],
    description: "Lecture factuelle d'un bail (habitation ou commercial) : montants, surfaces, dépôt de garantie, durée, conditions particulières, diagnostics présents ou manquants.",
    bullets: [
      "Loyer, charges, dépôt de garantie, mode de révision",
      "Surface déclarée vs surface diagnostiquée",
      "Inventaire des diagnostics présents et manquants (DPE, plomb, amiante, électricité, gaz, ERP)",
      "Durée, reconduction, conditions de résiliation",
      "Chronologie des étapes (signature, effet, état des lieux)",
    ],
    vertical_id: "immobilier",
    faq: [
      { q: "Le rapport peut-il être utilisé devant un tribunal ?", a: "CONFILEX produit une lecture factuelle horodatée. Le rapport n'a pas valeur de constat probatoire ; seul un commissaire de justice peut produire un constat opposable. Le rapport reste utile pour préparer un dossier avec un avocat." },
      { q: "Mon bail commercial peut-il être analysé ?", a: "Oui — le Vérificateur identifie les clauses récurrentes (durée, paliers de loyer, dépôt, charges récupérables) et signale les éventuelles divergences entre pièces." },
    ],
  },
  "verifier-une-facture": {
    h1: "Vérifier une facture commerciale",
    seo_title: "Vérifier une facture (entreprise, TVA, mentions) — CONFILEX",
    seo_description: "Audit factuel d'une facture : mentions obligatoires, TVA, rapprochement avec un devis ou contrat. Lecture documentaire CONFILEX.",
    icon: Receipt,
    intent_keywords: ["vérifier une facture", "audit de facture", "facture TVA", "mentions obligatoires facture"],
    description: "Lecture documentaire d'une facture : présence des mentions obligatoires, cohérence des montants HT/TTC/TVA, rapprochement possible avec un devis ou un contrat.",
    bullets: [
      "Mentions obligatoires (raison sociale, SIREN, numéro TVA, numéro de facture, date)",
      "Cohérence arithmétique HT / TVA / TTC",
      "Rapprochement avec un éventuel devis associé",
      "Détection de divergences entre facture et bon de commande",
      "Identification des conditions de paiement déclarées",
    ],
    vertical_id: "comptable",
    faq: [
      { q: "Quelles mentions obligatoires sont vérifiées ?", a: "Les mentions factuellement attendues sur une facture commerciale française : raison sociale, SIREN, adresse, numéro TVA intracommunautaire, date d'émission, numéro de facture, désignation des produits/services, prix unitaires HT, taux et montant TVA, montant TTC, conditions de règlement." },
    ],
  },
  "verifier-un-devis": {
    h1: "Vérifier un devis avant signature",
    seo_title: "Vérifier un devis (artisan, prestataire) — CONFILEX",
    seo_description: "Audit documentaire d'un devis : prix, prestations, conditions, TVA, mentions obligatoires. Repérage des points à clarifier avant signature.",
    icon: FileSpreadsheet,
    intent_keywords: ["vérifier un devis", "audit de devis", "devis artisan", "devis prestataire"],
    description: "Lecture documentaire d'un devis artisan ou prestataire : prestations décrites, prix unitaire et global, conditions, mentions obligatoires.",
    bullets: [
      "Inventaire des prestations décrites et de leur quantification",
      "Cohérence arithmétique unitaire / global",
      "Présence des mentions obligatoires (durée validité, conditions, TVA)",
      "Identification des conditions particulières (acompte, délai, garantie)",
      "Points à clarifier avant signature",
    ],
    vertical_id: "comptable",
    faq: [
      { q: "Peut-on comparer plusieurs devis ?", a: "L'Audit transverse CONFILEX permet de croiser plusieurs documents du même dossier (par exemple 3 devis pour le même chantier). Le moteur signale les divergences de prix, de prestations et de conditions entre les pièces." },
    ],
  },
  "verifier-documents-entreprise": {
    h1: "Audit documentaire d'une entreprise",
    seo_title: "Vérifier les documents d'une entreprise — CONFILEX",
    seo_description: "Audit documentaire transverse d'un dossier d'entreprise : statuts, comptes annuels, bilan, K-bis, contrats commerciaux. Lecture comparative entre pièces.",
    icon: Building2,
    intent_keywords: ["audit documentaire entreprise", "K-bis statuts comptes annuels", "due diligence light"],
    description: "Lecture documentaire transverse d'un dossier d'entreprise : croisement entre K-bis, statuts, comptes annuels, contrats. L'Audit transverse CONFILEX consolide les pièces et identifie les divergences.",
    bullets: [
      "Inventaire des pièces transmises (K-bis, statuts, comptes, contrats)",
      "Cohérence entre statuts et K-bis (forme sociale, capital, dirigeants)",
      "Lecture des comptes annuels (CA, marge, résultat, capitaux propres)",
      "Repérage des conventions réglementées identifiées",
      "Croisement multi-documents avec l'Audit transverse",
    ],
    vertical_id: "gouvernance",
    faq: [
      { q: "Combien de documents puis-je croiser ?", a: "Jusqu'à 12 sessions Vérificateur payées simultanément via l'Audit transverse. Chaque pièce est d'abord analysée individuellement par le Vérificateur, puis les analyses sont croisées par le moteur déterministe CONFILEX." },
    ],
  },
};


export default function VerifierVerticalPage({ slug }) {
  const cfg = VERIFIER_PAGES[slug];
  if (!cfg) return <div className="p-20 text-center">Page non trouvée.</div>;
  const Icon = cfg.icon || ScanLine;

  return (
    <>
      <Seo
        title={cfg.seo_title}
        description={cfg.seo_description}
        canonical={`/${slug}`}
        keywords={cfg.intent_keywords}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": cfg.h1,
            "description": cfg.seo_description,
            "provider": { "@type": "Organization", "name": "CONFILEX", "url": "https://confilex.fr" },
            "areaServed": { "@type": "Country", "name": "France" },
            "serviceType": "Audit documentaire",
            "termsOfService": "https://confilex.fr/legal",
          },
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Vérificateur", url: "/verifier" },
            { name: cfg.h1, url: `/${slug}` },
          ]),
          ldFAQ(cfg.faq),
        ]}
      />

      <main className="bg-paper min-h-screen" data-testid={`verifier-vertical-${slug}`}>
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              CONFILEX · AUDIT DOCUMENTAIRE INTELLIGENT
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="page-h1">
              {cfg.h1}
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed mb-6">
              {cfg.description}
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to={`/verifier${cfg.vertical_id ? `?vertical=${cfg.vertical_id}` : ""}`}>
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm" data-testid="cta-verifier">
                  <ScanLine className="w-4 h-4 mr-2" /> Démarrer l'analyse documentaire
                </Button>
              </Link>
              <Link to="/audit/transverse">
                <Button variant="outline" className="border-paper/40 text-paper hover:bg-paper/10 rounded-sm" data-testid="cta-transverse">
                  <GitMerge className="w-4 h-4 mr-2" /> Croiser plusieurs pièces
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
            CE QUE LE VÉRIFICATEUR IDENTIFIE
          </p>
          <h2 className="font-serif italic text-3xl text-night mb-6">Lecture documentaire spécialisée</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cfg.bullets.map((b, i) => (
              <Card key={i} className="p-4 border-sage hover:border-copper transition-smooth flex gap-3 items-start" data-testid={`feature-${i}`}>
                <Icon className="w-5 h-5 text-copper mt-0.5 flex-shrink-0" />
                <p className="text-sm text-night leading-relaxed">{b}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-14" data-testid="page-faq">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">QUESTIONS FRÉQUENTES</p>
          <h2 className="font-serif italic text-3xl text-night mb-8">À savoir avant de lancer une analyse</h2>
          <div className="space-y-5">
            {cfg.faq.map((item, i) => (
              <Card key={i} className="p-5 border-sage">
                <h3 className="font-semibold text-night mb-2">{item.q}</h3>
                <p className="text-sm text-night/75 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-ivory border-t border-sage">
          <div className="max-w-5xl mx-auto px-6 py-12 flex items-start gap-4">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-serif italic text-xl text-night mb-2">Vous voulez ensuite consulter un professionnel ?</h3>
              <p className="text-sm text-night/75 mb-4">
                CONFILEX peut vous orienter vers un avocat, expert-comptable, notaire, médiateur ou
                commissaire de justice qualifié pour votre dossier.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to="/trouver-un-avocat" className="inline-flex items-center text-sm text-copper hover:underline" data-testid="link-avocat">
                  Trouver un avocat <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
                <span className="text-night/30">·</span>
                <Link to="/trouver-un-expert-comptable" className="inline-flex items-center text-sm text-copper hover:underline" data-testid="link-ec">
                  Trouver un expert-comptable <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
                <span className="text-night/30">·</span>
                <Link to="/trouver-un-notaire" className="inline-flex items-center text-sm text-copper hover:underline" data-testid="link-notaire">
                  Trouver un notaire <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX réalise une analyse documentaire, factuelle et organisationnelle.
              Le rapport ne constitue ni une consultation juridique, ni un avis juridique,
              ni une consultation comptable, fiscale, notariale ou financière.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
